import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import { CheckIcon, ThumbsUpIcon, ChevronRightIcon, StarIcon } from 'lucide-react';
import { compareListings } from '../data/compareListings';
interface ServiceComparePageProps {
  serviceSlug: string;
  onNavigate: (path: string) => void;
}
export function ServiceComparePage({
  serviceSlug,
  onNavigate
}: ServiceComparePageProps) {
  const listingData = compareListings.find(l => l.serviceSlug === serviceSlug);
  if (!listingData) {
    return <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center p-4" data-id="element-2611">
        <h1 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-2612">
          Service not found
        </h1>
        <button onClick={() => onNavigate('/')} className="text-amber-600 hover:text-amber-700 font-medium flex items-center gap-2" data-id="element-2613">
          Return Home <ChevronRightIcon className="w-4 h-4" data-id="element-2614" />
        </button>
      </div>;
  }
  // Helper to get the top bar color based on rank
  const getTopBarColor = (rank: number) => {
    switch (rank) {
      case 1:
        return 'bg-[#E5B83B]';
      // Gold/Yellow
      case 2:
        return 'bg-[#E89E6A]';
      // Orange/Peach
      case 3:
        return 'bg-[#6CAFB7]';
      // Teal/Blue
      default:
        return 'bg-slate-300';
    }
  };
  // Helper to get a generic image based on service
  const getHeroImage = () => {
    const images: Record<string, string> = {
      'window-replacement': 'https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=800&q=80',
      roofing: 'https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=800&q=80',
      'bathroom-remodel': 'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=800&q=80',
      'kitchen-remodel': 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=800&q=80'
    };
    return images[serviceSlug] || 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=800&q=80';
  };
  return <div className="min-h-screen bg-[#F4F7F6] font-sans pb-20" data-id="element-2615">
      {/* Breadcrumb Navigation */}
      <div className="bg-white py-3 border-b border-gray-200" data-id="element-2616">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center text-xs font-medium text-gray-500" data-id="element-2617">
          <button onClick={() => onNavigate('/')} className="hover:text-slate-900" data-id="element-2618">
            Home
          </button>
          <span className="mx-2" data-id="element-2619">/</span>
          <button onClick={() => onNavigate(`/near-me/${serviceSlug}`)} className="hover:text-slate-900" data-id="element-2620">
            {listingData.serviceName}
          </button>
          <span className="mx-2" data-id="element-2621">/</span>
          <span className="text-slate-900" data-id="element-2622">Best</span>
        </div>
      </div>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-10" data-id="element-2623">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row gap-8 mb-12 items-center" data-id="element-2624">
          <div className="flex-1" data-id="element-2625">
            <h1 className="text-4xl md:text-5xl font-serif text-slate-900 mb-4 leading-tight" data-id="element-2626">
              Best {listingData.serviceName} Of 2026
            </h1>
            <p className="text-lg text-slate-700 mb-6 leading-relaxed" data-id="element-2627">
              {listingData.description}
            </p>
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700" data-id="element-2628">
              <div className="w-5 h-5 rounded-full bg-[#4DB6AC] flex items-center justify-center" data-id="element-2629">
                <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-2630" />
              </div>
              Last Updated Mar 2026
            </div>
          </div>
          <div className="w-full md:w-1/3 lg:w-2/5" data-id="element-2631">
            <img src={getHeroImage()} alt={`${listingData.serviceName} illustration`} className="w-full h-64 object-cover rounded-xl shadow-md" data-id="element-2632" />
          </div>
        </div>

        {/* Listings */}
        <div className="space-y-8" data-id="element-2633">
          {listingData.companies.map((company, index) => <motion.div key={company.name} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-visible relative" data-id="element-2634">
              {/* Top Bar */}
              <div className={`${getTopBarColor(company.rank)} h-12 flex items-center justify-between pr-16 rounded-t-xl relative`} data-id="element-2635">
                <div className="flex items-center h-full" data-id="element-2636">
                  <div className="bg-[#333333] text-white w-12 h-full flex items-center justify-center font-bold text-lg rounded-tl-xl" data-id="element-2637">
                    {company.rank}
                  </div>
                  <div className="bg-[#D32F2F] text-white px-3 py-1 ml-2 text-xs font-bold uppercase tracking-wider rounded-sm" data-id="element-2638">
                    {company.badge}
                  </div>
                </div>
                <div className="text-white font-bold text-sm md:text-base hidden sm:block" data-id="element-2639">
                  {company.badge === 'TOP PICK' ? 'Outstanding' : company.badge}
                </div>

                {/* Score Circle */}
                <div className="absolute right-4 top-1/2 -translate-y-1/2 translate-x-1/2 md:translate-x-0 md:-right-6 w-14 h-14 bg-white rounded-full border-4 flex items-center justify-center font-bold text-xl text-slate-900 shadow-sm z-10" style={{
              borderColor: company.rank === 1 ? '#E5B83B' : company.rank === 2 ? '#E89E6A' : '#6CAFB7'
            }} data-id="element-2640">
                  {company.score.toFixed(1)}
                </div>
              </div>

              {/* Sub Bar */}
              <div className="bg-[#333333] text-white text-center py-1.5 text-xs font-medium flex items-center justify-center gap-2" data-id="element-2641">
                <ThumbsUpIcon className="w-3 h-3" data-id="element-2642" />
                {Math.floor(Math.random() * 2000 + 1000).toLocaleString()}{' '}
                people visited this week
              </div>

              {/* Main Content */}
              <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center md:items-start" data-id="element-2643">
                {/* Logo / Name Area */}
                <div className="w-full md:w-1/4 flex flex-col items-center justify-center text-center border-b md:border-b-0 md:border-r border-gray-100 pb-6 md:pb-0 md:pr-6" data-id="element-2644">
                  <h3 className="text-2xl font-serif font-bold text-[#1A365D] mb-3 tracking-tight" data-id="element-2645">
                    {company.name.toUpperCase()}
                  </h3>
                  <div className="flex text-[#F4B400] gap-1" data-id="element-2646">
                    {[...Array(5)].map((_, i) => <StarIcon key={i} className="w-5 h-5" fill={i < company.stars ? 'currentColor' : 'none'} strokeWidth={i < company.stars ? 0 : 2} color={i < company.stars ? undefined : '#E0E0E0'} data-id="element-2647" />)}
                  </div>
                </div>

                {/* Features List */}
                <div className="w-full md:w-1/2" data-id="element-2648">
                  <ul className="space-y-3" data-id="element-2649">
                    {company.features.map((feature, idx) => <li key={idx} className="flex items-start gap-3 text-sm text-slate-700 font-medium" data-id="element-2650">
                        <CheckIcon className="w-5 h-5 text-[#4DB6AC] flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-2651" />
                        <span data-id="element-2652">{feature}</span>
                      </li>)}
                  </ul>
                </div>

                {/* CTA Area */}
                <div className="w-full md:w-1/4 flex flex-col items-center justify-center text-center space-y-4" data-id="element-2653">
                  <button onClick={() => onNavigate(`/${serviceSlug}/quote-form`)} className="w-full bg-[#D32F2F] hover:bg-[#B71C1C] text-white font-bold py-3.5 px-6 rounded-md transition-colors shadow-md text-sm md:text-base" data-id="element-2654">
                    See Details
                  </button>
                  <div className="text-sm font-bold text-slate-900 leading-snug" data-id="element-2655">
                    {company.offer.split('+').map((part, i) => <Fragment key={i} data-id="element-2656">
                        {part.trim()}
                        {i < company.offer.split('+').length - 1 && <br data-id="element-2657" />}
                      </Fragment>)}
                  </div>
                </div>
              </div>

              {/* Bottom Link */}
              <div className="border-t border-gray-100 py-4 text-center bg-gray-50/50 rounded-b-xl" data-id="element-2658">
                <button onClick={() => onNavigate(`/${serviceSlug}/quote-form`)} className="text-xs font-bold text-slate-900 hover:text-[#D32F2F] uppercase tracking-wider underline decoration-2 underline-offset-4 transition-colors" data-id="element-2659">
                  Read Our {company.name} Review
                </button>
              </div>
            </motion.div>)}
        </div>
      </main>
    </div>;
}